import React, { useEffect, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate, useLocation } from "react-router-dom";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Icons
import {
  UploadCloud,
  AlertTriangle,
  Plus,
  Trash2,
  CircleX,
  EyeIcon,
} from "lucide-react";

// File Upload Components
import {
  FileUploadZone,
  FileErrorMessage,
  AdminOnlyWarning,
  SingleFileDisplay,
  ExistingFileDisplay,
  RemovedFileMessage,
  MultipleFilesList,
  ExistingFilesList,
  CompactExistingFilesList,
  CompactMultipleFilesList,
} from "./FileUploadComponents";

// Context and Hooks
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";

// Utils
import {
  sanitizeFilenameForAzure,
  validateAzureFilename,
  createFileWithSanitizedName,
} from "@/utils/fileUtils";

// Types
import type {
  PackagingMaterial,
  PlasticType,
  StorageType,
  PalletMaterial,
  RackAccessory,
  StorageMethod,
  SprinklerSystemType,
  Commodity,
} from "@/types";
import { CommodityCard } from "./CommodityCard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreateSubmissionRequestDto } from "@/dto/create-submission-request.dto";
import { projectApi } from "@/services/projectService";
import { draftSubmissionService } from "@/services/draftSubmissionService";

import {
  GetProjectResponseDto,
  IDocument,
} from "@/dto/get-project-response.dto";
import { Roles } from "@/enums/roles.enum";

// Constants definitions
const packagingTypes = [
  { value: "loose-bag", label: "Loose Bag" },
  { value: "retail-carton", label: "Retail Carton" },
  { value: "corrugated-carton", label: "Corrugated Carton" },
  { value: "other", label: "Other" },
];

const packagingMaterials: { value: PackagingMaterial; label: string }[] = [
  { value: "plastic", label: "Plastic" },
  { value: "cardboard", label: "Cardboard" },
  { value: "wood", label: "Wood" },
  { value: "metal", label: "Metal" },
  { value: "glass", label: "Glass" },
  { value: "other", label: "Other" },
];

const plasticTypes: { value: PlasticType; label: string }[] = [
  { value: "abs", label: "ABS" },
  { value: "acetal", label: "Acetal" },
  { value: "acrylic", label: "Acrylic" },
  { value: "butyl-rubber", label: "Butyl Rubber" },
  { value: "pet", label: "PET" },
  { value: "polyethylene", label: "Polyethylene" },
  { value: "polypropylene", label: "Polypropylene" },
  { value: "polystyrene", label: "Polystyrene" },
  { value: "polyurethane", label: "Polyurethane" },
  { value: "pvc", label: "PVC" },
  { value: "other", label: "Other" },
];

const storageTypes = [
  { value: "single-row-pallet-racks", label: "Single-row pallet racks" },
  { value: "double-row-pallet-racks", label: "Double-row pallet racks" },
  { value: "multiple-row-racks-small", label: "Multiple-row racks ≤16′ depth" },
  { value: "multiple-row-racks-large", label: "Multiple-row racks >16′ depth" },
  {
    value: "palletized-solid-pile",
    label: "Palletized/solid pile (floor stack)",
  },
  { value: "shelf-storage", label: "Shelf storage / Back-to-back shelves" },
  { value: "catwalk-system", label: "Catwalk system" },
  { value: "under-mezzanine-storage", label: "Under-mezzanine storage" },
  { value: "other", label: "Other" },
];

const rackAccessories = [
  { value: "wire-mesh-grating", label: "Wire mesh grating" },
  { value: "pallet-supports", label: "Pallet supports" },
  { value: "slatted-shelves", label: "Slatted shelves" },
  { value: "open-shelf", label: "Open shelf" },
];

// Validation constants
const PALLET_DIMENSIONS_REGEX = /^\d{2,3}"\s*x\s*\d{2,3}"\s*x\s*\d{2,3}"$/;
const REQUIRED_FILES = [
  "po",
  "bom",
  "sprinklerInfo",
  "floorPlanElevations",
] as const;

// Schema for commodity
const commoditySchema = z
  .object({
    id: z.string().optional(),
    productName: z.string().min(1, "Product stored is required"),
    packagingType: z.string(),
    packagingMaterials: z
      .array(z.string())
      .min(1, "Select at least one packaging material"),
    isFullyEncapsulated: z.boolean(),
    canBeReconfigured: z.boolean().optional(),
    reconfigurationNotes: z.string().optional().or(z.literal("")),
    plasticTypes: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    // If canBeReconfigured, require reconfigurationNotes
    if (
      data.canBeReconfigured &&
      (!data.reconfigurationNotes ||
        data.reconfigurationNotes.trim().length < 10)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Note is required (min 10 chars)",
        path: ["reconfigurationNotes"],
      });
    }
    // If packagingMaterials includes "plastic", require at least one plasticType
    if (
      data.packagingMaterials?.includes("plastic") &&
      (!data.plasticTypes || data.plasticTypes.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one plastic type",
        path: ["plasticTypes"],
      });
    }
  });

// Form validation schema
const submissionFormSchema = z
  .object({
    // Section 1: Project Valuation & PO Details
    projectValuation: z
      .number({ invalid_type_error: "Project valuation must be a number" })
      .min(0, "Project valuation is required"),
    poNumber: z.string().nonempty("PO Number is required"),

    // Section 2: Site & Safety Assessments
    isFireHazardZone: z.boolean(),
    fireHazardDetails: z.string().optional(),
    isPubliclyAccessible: z.boolean(),
    hasLiquidPropaneTankStorage: z.boolean(),

    // Section 3: Commodity Details
    commodities: z.array(commoditySchema).min(0),
    hasHazardousMaterials: z.boolean(),
    hazardousMaterialsDetails: z.string().optional(),

    // Section 4: Storage Configuration
    storageTypes: z
      .array(z.string() as z.ZodType<StorageType>)
      .min(1, "Select at least one storage type"),
    otherStorageTypeDetails: z.string().optional(),
    palletDimensions: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === "") return true; // Allow empty
          return PALLET_DIMENSIONS_REGEX.test(val);
        },
        {
          message: 'Format: e.g., 48" x 40" x 60"',
        }
      ),
    hasOversizedLoads: z.boolean(),
    oversizedLoadsDetails: z.string().optional(),
    palletMaterial: z.enum(["wood", "plastic"]),
    rackAccessories: z.array(z.string() as z.ZodType<RackAccessory>),

    // Section 5: Handling & Operations
    storageMethod: z.enum(["hand-stacked", "mechanical"]),
    hasIdlePalletStorage: z.boolean(),
    idlePalletMaterial: z.enum(["wood", "plastic"]).optional(),

    // Section 6: Sprinkler & Fire Protection
    buildingHeight: z
      .number({
        invalid_type_error: "Building height must be a number (in feet)",
      })
      .min(1, "Building height is required and must be greater than 0"),
    sprinklerSystemType: z.enum(["esfr", "cmsa", "cmda", "not-sure"]),
    kFactorPsi: z.string().optional(),
    densityRequirement: z.string().optional(),

    // Section 7: Engineering & Documents
    peStampRequired: z.enum(["no", "yes", "not-sure"]),

    contractorCompany: z.string().min(1, "Contractor company is required"),
    contractorPhone: z
      .string()
      .min(1, "Contractor phone is required")
      .regex(/^[0-9+\-\s()]+$/, "Enter a valid phone number"),
    contractorAddress: z.string().min(1, "Contractor address is required"),
    contractorLicenseNo: z.string().optional(),

    enterCommodity: z.boolean(),
    uploadLicense: z.boolean(),
    files: z.object({
      commodityLetter: z.instanceof(File).optional().nullable(),
      sprinklerInfo: z.array(z.instanceof(File)).optional().nullable(), // Now array for multiple files
      cadFile: z.instanceof(File).optional().nullable(),
      projectValuationDocs: z.instanceof(File).optional().nullable(),
      po: z.instanceof(File).optional().nullable(), // Removed required
      bom: z.instanceof(File).optional().nullable(), // Removed required
      contractorLicense: z.instanceof(File).optional().nullable(),
      architecturalFile: z.array(z.instanceof(File)).optional().nullable(), // Now array for multiple files
      floorPlanElevations: z.array(z.instanceof(File)).optional().nullable(), // Multiple PDFs/DWGs
    }),
  })

  .refine(
    (data) =>
      !data.isFireHazardZone ||
      (data.fireHazardDetails && data.fireHazardDetails.trim().length >= 10),
    {
      message: "Fire hazard details are required (min 10 chars)",
      path: ["fireHazardDetails"],
    }
  )
  .refine(
    (data) =>
      !data.hasOversizedLoads ||
      (data.oversizedLoadsDetails &&
        data.oversizedLoadsDetails.trim().length >= 10),
    {
      message: "Oversized loads details are required (min 10 chars)",
      path: ["oversizedLoadsDetails"],
    }
  )
  .refine(
    (data) =>
      !data.hasHazardousMaterials ||
      (data.hazardousMaterialsDetails &&
        data.hazardousMaterialsDetails.trim().length >= 10),
    {
      message: "Hazardous materials details are required (min 10 chars)",
      path: ["hazardousMaterialsDetails"],
    }
  )
  .refine(
    (data) =>
      !["esfr", "cmsa"].includes(data.sprinklerSystemType) ||
      (!!data.kFactorPsi &&
        /^K\d+(\.\d+)?\s*@\s*\d+(\.\d+)?\s*psi$/i.test(data.kFactorPsi)),
    {
      message:
        "K-Factor/PSI is required and must be in format: e.g., K14 @ 50 psi",
      path: ["kFactorPsi"],
    }
  )
  .refine(
    (data) =>
      !["esfr", "cmsa"].includes(data.sprinklerSystemType) ||
      (!!data.kFactorPsi &&
        /^K\d+(\.\d+)?\s*@\s*\d+(\.\d+)?\s*psi$/i.test(data.kFactorPsi)),
    {
      message:
        "K-Factor/PSI is required and must be in format: e.g., K14 @ 50 psi",
      path: ["kFactorPsi"],
    }
  )
  .refine(
    (data) =>
      data.sprinklerSystemType !== "cmda" ||
      (!!data.densityRequirement &&
        /^\d+(\.\d+)?\s*gpm\/sq\s*ft$/i.test(data.densityRequirement)),
    {
      message:
        "Density requirement is required and must be in format: e.g., 0.45 gpm/sq ft",
      path: ["densityRequirement"],
    }
  );

export type SubmissionFormValues = z.infer<typeof submissionFormSchema>;

const DEFAULT_FORM_VALUES: SubmissionFormValues = {
  projectValuation: 0,
  poNumber: "",
  isFireHazardZone: false,
  fireHazardDetails: "",
  isPubliclyAccessible: false,
  hasLiquidPropaneTankStorage: false,
  commodities: [],
  hasHazardousMaterials: false,
  hazardousMaterialsDetails: "",
  storageTypes: [],
  otherStorageTypeDetails: "",
  palletDimensions: "",
  hasOversizedLoads: false,
  oversizedLoadsDetails: "",
  palletMaterial: "wood",
  rackAccessories: [],
  storageMethod: "mechanical",
  hasIdlePalletStorage: false,
  idlePalletMaterial: undefined,
  sprinklerSystemType: "esfr",
  kFactorPsi: "",
  densityRequirement: "",
  buildingHeight: 30,
  contractorCompany: "",
  contractorPhone: "",
  contractorAddress: "",
  contractorLicenseNo: "",
  peStampRequired: "not-sure",
  enterCommodity: false,
  uploadLicense: false,
  files: {
    commodityLetter: null,
    sprinklerInfo: [],
    cadFile: null,
    projectValuationDocs: null,
    po: null,
    bom: null,
    contractorLicense: null,
    architecturalFile: [],
    floorPlanElevations: [],
  },
};

interface SubmissionFormProps {
  projectId: string;
  projectDetails?: GetProjectResponseDto;
}

const SubmissionForm = ({ projectId, projectDetails }: SubmissionFormProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Check if we're on the "new submission" route (autosave should only work here)
  const isNewSubmissionRoute = location.pathname.includes("/submissions/new");

  // Form
  const form = useForm<SubmissionFormValues>({
    shouldFocusError: true,
    mode: "onChange",
    resolver: zodResolver(submissionFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const watchIsFireHazardZone = form.watch("isFireHazardZone");
  const watchIsPubliclyAccessible = form.watch("isPubliclyAccessible");
  const watchHasLiquidPropaneTankStorage = form.watch(
    "hasLiquidPropaneTankStorage"
  );
  const watchHasOversizedLoads = form.watch("hasOversizedLoads");
  const watchHasHazardousMaterials = form.watch("hasHazardousMaterials");
  const enterCommodity = form.watch("enterCommodity");
  const uploadLicense = form.watch("uploadLicense");
  const watchStorageMethod = form.watch("storageMethod");
  const watchHasIdlePalletStorage = form.watch("hasIdlePalletStorage");
  const watchIdlePalletMaterial = form.watch("idlePalletMaterial");
  const watchSprinklerType = form.watch("sprinklerSystemType");
  const watchStorageTypes = form.watch("storageTypes");
  const commoditiesWatch = form.watch("commodities");
  const commodityLetterFile = form.watch("files.commodityLetter");
  const sprinklerInfoFile = form.watch("files.sprinklerInfo");
  const cadFile = form.watch("files.cadFile");
  const projectValuationFile = form.watch("files.projectValuationDocs");
  const poFile = form.watch("files.po");
  const bomFile = form.watch("files.bom");
  const licenseFile = form.watch("files.contractorLicense");
  const architecturalFile = form.watch("files.architecturalFile");
  const floorPlanElevationsFile = form.watch("files.floorPlanElevations");

  // Only reset the form once when projectDetails.submission is first loaded
  const hasResetRef = React.useRef(false);

  // State for tracking removed files (admin only)
  const [removedFiles, setRemovedFiles] = React.useState<Set<string>>(
    new Set()
  );

  // State for tracking removed existing files from arrays (by blob name)
  const [removedExistingFiles, setRemovedExistingFiles] = React.useState<
    Set<string>
  >(new Set());

  // State for storing draft file metadata (similar to existing submission files)
  interface DraftFileMetadata {
    name: string;
    url: string;
    blobName: string;
    type?: string;
    size?: number;
  }

  const [draftFiles, setDraftFiles] = React.useState<
    Partial<
      Record<
        keyof SubmissionFormValues["files"],
        DraftFileMetadata | DraftFileMetadata[]
      >
    >
  >({});

  const isAdmin = user?.role === Roles.ADMIN;

  // Autosave functionality - only enabled on new submission route AND no existing submission
  const isNewSubmission = !projectDetails?.submission;
  const shouldEnableAutosave = isNewSubmissionRoute && isNewSubmission;

  console.log("Autosave status:", {
    isNewSubmissionRoute,
    isNewSubmission,
    shouldEnableAutosave,
    pathname: location.pathname,
  }); // Debug log

  const [lastSaveTime, setLastSaveTime] = React.useState<Date | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = React.useState(false);

  // Refs for debouncing
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRequestInProgressRef = useRef(false);
  const isInitialLoadRef = useRef(false); // Flag to prevent autosave during initial draft load

  // Ref to track current removedFiles set (avoids closure issues)
  const removedFilesRef = useRef<Set<string>>(removedFiles);

  // Update ref whenever removedFiles state changes
  React.useEffect(() => {
    removedFilesRef.current = removedFiles;
  }, [removedFiles]);

  // Function to save draft to backend
  const saveDraftToBackend = useCallback(
    async (
      formData: SubmissionFormValues,
      files: Record<string, File | File[]> = {}
    ) => {
      // Don't save if: not enabled, already saving, loading draft, or request in progress
      if (
        !shouldEnableAutosave ||
        isSaving ||
        isLoadingDraft ||
        isRequestInProgressRef.current
      ) {
        console.log("Skipping saveDraftToBackend:", {
          shouldEnableAutosave,
          isSaving,
          isLoadingDraft,
          isRequestInProgress: isRequestInProgressRef.current,
        }); // Debug log
        return;
      }

      try {
        setIsSaving(true);
        isRequestInProgressRef.current = true;

        // Get current removedFiles from ref (avoids stale closure)
        const currentRemovedFiles = removedFilesRef.current;

        // Exclude files from form data, handle them separately
        const { files: _, ...dataToSave } = formData;

        // Create FormData for the request
        const requestFormData = draftSubmissionService.createFormDataFromValues(
          dataToSave,
          files
        );

        // Add removed files to FormData so backend can delete them
        if (currentRemovedFiles.size > 0) {
          requestFormData.append(
            "removedFiles",
            JSON.stringify(Array.from(currentRemovedFiles))
          );
        }

        await draftSubmissionService.saveDraft(projectId, requestFormData);

        // Clear removed files after successful save
        if (currentRemovedFiles.size > 0) {
          setRemovedFiles(new Set());
        }

        // If files were uploaded, reload draft to get updated file metadata
        if (Object.keys(files).length > 0) {
          try {
            const response = await draftSubmissionService.getDraft(projectId);
            if (response.draft && response.draft.files) {
              // Update draftFiles state with new file metadata
              setDraftFiles(
                response.draft.files as unknown as typeof draftFiles
              );

              // Clear File objects from form state for uploaded files
              Object.keys(files).forEach((fieldName) => {
                const fileKey =
                  `files.${fieldName}` as keyof SubmissionFormValues;
                form.setValue(fileKey, [] as never);
              });
            }
          } catch (error) {
            console.error("Failed to reload draft after file upload:", error);
          }
        }

        setLastSaveTime(new Date());
      } catch (error) {
        console.error("Failed to save draft to backend:", error);
        // Don't show error toast for autosave failures to avoid user annoyance
      } finally {
        setIsSaving(false);
        isRequestInProgressRef.current = false;
      }
    },
    [projectId, shouldEnableAutosave, isSaving, isLoadingDraft, form]
  );

  // Helper to check if form has meaningful data
  const hasMeaningfulData = useCallback((formData: SubmissionFormValues) => {
    return (
      !!formData.poNumber ||
      formData.projectValuation > 0 ||
      !!formData.contractorCompany ||
      !!formData.contractorAddress ||
      !!formData.contractorPhone ||
      !!formData.contractorLicenseNo ||
      formData.commodities?.length > 0 ||
      formData.storageTypes?.length > 0 ||
      !!formData.kFactorPsi ||
      !!formData.densityRequirement ||
      !!formData.fireHazardDetails ||
      !!formData.hazardousMaterialsDetails ||
      !!formData.oversizedLoadsDetails ||
      !!formData.otherStorageTypeDetails ||
      !!formData.palletDimensions ||
      !!formData.isFireHazardZone ||
      !!formData.isPubliclyAccessible ||
      !!formData.hasLiquidPropaneTankStorage ||
      !!formData.hasHazardousMaterials ||
      !!formData.hasOversizedLoads ||
      !!formData.hasIdlePalletStorage ||
      !!formData.enterCommodity ||
      !!formData.uploadLicense ||
      formData.peStampRequired !== "not-sure" ||
      formData.buildingHeight !== 30 ||
      formData.sprinklerSystemType !== "esfr" ||
      formData.storageMethod !== "mechanical" ||
      formData.palletMaterial !== "wood" ||
      !!formData.idlePalletMaterial ||
      formData.rackAccessories?.length > 0
    );
  }, []);

  // Debounced save function
  const debouncedSave = useCallback(
    (
      formData: SubmissionFormValues,
      files: Record<string, File | File[]> = {},
      delay: number = 2000
    ) => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        // Check if form has any meaningful data to save
        // We check multiple fields to ensure we capture partial entries (e.g. just uploading a file or filling address)
        const hasFiles = Object.keys(files).length > 0;
        const hasData = hasMeaningfulData(formData);

        if (hasData || hasFiles) {
          console.log("Saving draft to backend...", { hasData, hasFiles }); // Debug log
          saveDraftToBackend(formData, files);
        } else {
          console.log("Skipping save: No meaningful data"); // Debug log
        }
      }, delay);
    },
    [saveDraftToBackend, hasMeaningfulData]
  );

  // Function to load draft from backend
  const loadDraftFromBackend = useCallback(async () => {
    if (!shouldEnableAutosave) return null;

    try {
      setIsLoadingDraft(true);
      isInitialLoadRef.current = true; // Set flag to prevent autosave during load
      const response = await draftSubmissionService.getDraft(projectId);
      return response.draft;
    } catch (error) {
      // 404 is expected when no draft exists
      if (error?.response?.status !== 404) {
        console.error("Failed to load draft from backend:", error);
      }
      return null;
    } finally {
      setIsLoadingDraft(false);
      // Keep the flag set for a bit longer to allow form.reset to complete
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100); // Small delay to ensure all form.watch calls from reset have processed
    }
  }, [projectId, shouldEnableAutosave]);

  // Function to clear draft from backend
  const clearDraftFromBackend = useCallback(async () => {
    if (!shouldEnableAutosave) return;

    try {
      await draftSubmissionService.deleteDraft(projectId);
      setLastSaveTime(null);
    } catch (error) {
      // 404 is expected when no draft exists
      if (error?.response?.status !== 404) {
        console.error("Failed to clear draft from backend:", error);
      }
    }
  }, [projectId, shouldEnableAutosave]);

  // Function to manually clear draft and reset form
  const clearDraft = useCallback(() => {
    if (!isNewSubmission) return;

    clearDraftFromBackend();
    form.reset();
    toast({
      title: "Draft Cleared",
      description: "Form has been reset to defaults.",
    });
  }, [isNewSubmission, clearDraftFromBackend, form, toast]);

  // Function to handle file removal (for existing submissions: admin only, for drafts: anyone)
  const handleRemoveExistingFile = useCallback(
    (fileKey: string) => {
      // For existing submissions, only admins can remove files
      if (projectDetails?.submission && !isAdmin) {
        return;
      }

      // For draft files or admin removing submission files
      setRemovedFiles((prev) => {
        const newSet = new Set(prev).add(fileKey);
        return newSet;
      });

      // Also remove from draft files state if it exists there
      if (draftFiles[fileKey as keyof typeof draftFiles]) {
        setDraftFiles((prev) => {
          const newDraftFiles = { ...prev };
          delete newDraftFiles[fileKey as keyof typeof draftFiles];
          return newDraftFiles;
        });
      }

      // Trigger autosave immediately when file is removed
      if (shouldEnableAutosave) {
        const formData = form.getValues() as SubmissionFormValues;
        const files: Record<string, File | File[]> = {};

        if (formData.files) {
          Object.entries(formData.files).forEach(([key, value]) => {
            if (value && value instanceof File) {
              files[key] = value;
            } else if (Array.isArray(value) && value.length > 0) {
              const fileArray = value.filter((item) => item instanceof File);
              if (fileArray.length > 0) {
                files[key] = fileArray;
              }
            }
          });
        }

        // Use debounced save to allow multiple deletions
        debouncedSave(formData, files, 500);
      }
    },
    [
      projectDetails?.submission,
      isAdmin,
      draftFiles,
      shouldEnableAutosave,
      form,
      debouncedSave,
    ]
  );

  // Function to handle removing existing files from arrays (by blob name)
  const handleRemoveExistingArrayFile = (blobName: string) => {
    if (!isAdmin) return;
    setRemovedExistingFiles((prev) => new Set(prev).add(blobName));
  };

  // Helper function to determine if a file is required
  const isFileRequired = (
    fileKey: keyof SubmissionFormValues["files"]
  ): boolean => {
    if (REQUIRED_FILES.includes(fileKey as (typeof REQUIRED_FILES)[number])) {
      return true;
    }

    if (fileKey === "commodityLetter" && !enterCommodity) {
      return true; // Required if not entering commodity manually
    }

    if (fileKey === "contractorLicense" && uploadLicense) {
      return true; // Required if user checked upload license
    }

    return false; // Optional files: projectValuationDocs, cadFile, architecturalFile
  };

  // Helper function to determine if delete button should be shown
  const shouldShowDeleteButton = (
    fileKey: keyof SubmissionFormValues["files"],
    isNewFile: boolean,
    hasExistingFile: boolean
  ): boolean => {
    // Always allow deleting new files
    if (isNewFile) {
      return true;
    }

    // For draft files (no project details submission), allow deletion of ALL files
    // Users can remove any file while working on a draft, validation happens on submit
    if (hasExistingFile && !projectDetails?.submission) {
      return true;
    }

    // For existing submission files, only show delete if:
    // - User is admin
    // - File is not required OR file is required but conditionally optional
    if (hasExistingFile && projectDetails?.submission && isAdmin) {
      return !isFileRequired(fileKey);
    }

    return false;
  };

  useEffect(() => {
    if (projectDetails && projectDetails.submission && !hasResetRef.current) {
      form.reset({
        projectValuation: projectDetails.submission.projectValuation || 0,
        poNumber: projectDetails.submission.poNumber || "",
        isFireHazardZone: projectDetails.submission.isFireHazardZone || false,
        fireHazardDetails: projectDetails.submission.fireHazardDetails || "",
        isPubliclyAccessible:
          projectDetails.submission.isPubliclyAccessible || false,
        hasLiquidPropaneTankStorage:
          projectDetails.submission.hasLiquidPropaneTankStorage || false,
        commodities: projectDetails.submission.commodities || [],
        hasHazardousMaterials:
          projectDetails.submission.hasHazardousMaterials || false,
        hazardousMaterialsDetails:
          projectDetails.submission.hazardousMaterialsDetails || "",
        storageTypes: projectDetails.submission.storageTypes || [],
        otherStorageTypeDetails:
          projectDetails.submission.otherStorageTypeDetails || "",
        palletDimensions: projectDetails.submission.palletDimensions || "",
        hasOversizedLoads: projectDetails.submission.hasOversizedLoads || false,
        oversizedLoadsDetails:
          projectDetails.submission.oversizedLoadsDetails || "",
        palletMaterial: projectDetails.submission.palletMaterial || "wood",
        rackAccessories: projectDetails.submission.rackAccessories || [],
        storageMethod: projectDetails.submission.storageMethod || "mechanical",
        hasIdlePalletStorage:
          projectDetails.submission.hasIdlePalletStorage || false,
        idlePalletMaterial:
          projectDetails.submission.idlePalletMaterial || undefined,
        sprinklerSystemType:
          projectDetails.submission.sprinklerSystemType || "esfr",
        kFactorPsi: projectDetails.submission.kFactorPsi || "",
        densityRequirement: projectDetails.submission.densityRequirement || "",
        buildingHeight: projectDetails.submission.buildingHeight || 30,
        contractorCompany: projectDetails.submission.contractorCompany || "",
        contractorPhone: projectDetails.submission.contractorPhone || "",
        contractorAddress: projectDetails.submission.contractorAddress || "",
        contractorLicenseNo:
          projectDetails.submission.contractorLicenseNo || "",
        peStampRequired:
          projectDetails.submission.peStampRequired || "not-sure",
        enterCommodity: projectDetails.submission.enterCommodity || false,
        uploadLicense: projectDetails.submission.uploadLicense || false,

        files: {
          commodityLetter: null,
          sprinklerInfo: null,
          cadFile: null,
          projectValuationDocs: null,
          po: null,
          bom: null,
          contractorLicense: null,
          architecturalFile: null,
          floorPlanElevations: null,
        },
      });
      hasResetRef.current = true;
    }
  }, [projectDetails, form]);

  // Load draft data for new submissions (only on /submissions/new route)
  useEffect(() => {
    if (shouldEnableAutosave && !hasResetRef.current) {
      loadDraftFromBackend().then((savedData) => {
        if (savedData) {
          // Store draft file metadata in separate state
          if (savedData.files && Object.keys(savedData.files).length > 0) {
            // Type assertion: backend returns metadata objects with url/blobName, not File objects
            setDraftFiles(savedData.files as unknown as typeof draftFiles);
          }

          // Merge saved data with default values, preserving file defaults
          const defaultFiles = {
            commodityLetter: null,
            sprinklerInfo: [],
            cadFile: null,
            projectValuationDocs: null,
            po: null,
            bom: null,
            contractorLicense: null,
            architecturalFile: [],
            floorPlanElevations: [],
          };

          // Merge with DEFAULT_FORM_VALUES to ensure all fields have valid defaults
          // This fixes issues where missing draft fields caused UI components (like RadioGroup) to break
          form.reset({
            ...DEFAULT_FORM_VALUES,
            ...savedData,
            files: defaultFiles,
          });

          toast({
            title: "Draft Restored",
            description: "Your previous submission draft has been restored.",
          });
        }
      });
      hasResetRef.current = true;
    }
  }, [shouldEnableAutosave, form, toast, loadDraftFromBackend]);

  // Save on form value changes (debounced) - only on /submissions/new route
  useEffect(() => {
    if (!shouldEnableAutosave) return;

    const subscription = form.watch((value, { name, type }) => {
      // Skip if initial load is in progress
      if (isInitialLoadRef.current) {
        return;
      }

      console.log("Form changed:", name, type); // Debug log

      // Extract files from formData and pass separately
      const typedFormData = value as SubmissionFormValues;
      const files: Record<string, File | File[]> = {};

      if (typedFormData.files) {
        Object.entries(typedFormData.files).forEach(([key, value]) => {
          // Check for single File
          if (value && value instanceof File) {
            files[key] = value;
          }
          // Check for array of Files
          else if (Array.isArray(value) && value.length > 0) {
            // Filter to ensure all items are Files
            const fileArray = value.filter((item) => item instanceof File);
            if (fileArray.length > 0) {
              files[key] = fileArray;
            }
          }
        });
      }

      // Determine delay based on field type
      let delay = 2000; // Default for text inputs

      if (name) {
        // Immediate save (short delay) for non-text inputs
        const isFile = name.startsWith("files.");
        const isCheckboxOrRadio = [
          "isFireHazardZone",
          "isPubliclyAccessible",
          "hasLiquidPropaneTankStorage",
          "hasHazardousMaterials",
          "hasOversizedLoads",
          "hasIdlePalletStorage",
          "enterCommodity",
          "uploadLicense",
          "sprinklerSystemType",
          "storageMethod",
          "palletMaterial",
          "idlePalletMaterial",
          "peStampRequired",
          "storageTypes",
          "rackAccessories",
          "packagingMaterials",
          "plasticTypes",
        ].some((field) => name === field || name.startsWith(field + "."));

        // Also check for array operations (add/remove commodity)
        const isArrayOperation =
          name === "commodities" ||
          name === "storageTypes" ||
          name === "rackAccessories";

        // Check for select/radio inside commodities
        const isCommodityNonText =
          name.startsWith("commodities") &&
          (name.endsWith("packagingType") ||
            name.endsWith("isFullyEncapsulated") ||
            name.endsWith("canBeReconfigured") ||
            name.endsWith("packagingMaterials") ||
            name.endsWith("plasticTypes"));

        if (
          isFile ||
          isCheckboxOrRadio ||
          isArrayOperation ||
          isCommodityNonText
        ) {
          delay = 500; // 500ms for immediate feel but safe
        }
      }

      console.log("Triggering debounced save with delay:", delay); // Debug log

      // Use debounced save function with files
      debouncedSave(typedFormData, files, delay);
    });

    return () => {
      subscription.unsubscribe();
      // Clean up timeout on unmount
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [shouldEnableAutosave, form, debouncedSave]);

  useEffect(() => {
    if (
      commoditiesWatch.length > 0 &&
      (form.formState.errors.files?.commodityLetter ||
        form.formState.errors.enterCommodity)
    ) {
      form.clearErrors("files.commodityLetter");
      form.clearErrors("enterCommodity");
    }
  }, [commoditiesWatch, form, enterCommodity]);

  useEffect(() => {
    if (commodityLetterFile) {
      form.clearErrors("enterCommodity");
    }
  }, [commodityLetterFile, form]);

  useEffect(() => {
    if (!uploadLicense) {
      form.clearErrors("uploadLicense");
    }
  }, [uploadLicense, form]);

  const {
    fields: commodities,
    append: appendCommodity,
    remove: removeCommodity,
  } = useFieldArray({
    control: form.control,
    name: "commodities",
  });

  useEffect(() => {
    // Only add a commodity if:
    // - enterCommodity is true
    // - commodities array is empty
    // - form has been initialized (hasResetRef.current)
    // - NOT loading from draft
    // - NOT loading from an existing submission (projectDetails?.submission)
    if (
      enterCommodity &&
      commodities.length === 0 &&
      hasResetRef.current &&
      !isLoadingDraft &&
      !projectDetails?.submission
    ) {
      addCommodity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterCommodity, commodities.length]);

  const addCommodity = () => {
    const newCommodity: Commodity = {
      id: uuidv4(),
      productName: "",
      packagingType: "corrugated-carton",
      packagingMaterials: ["cardboard"],
      isFullyEncapsulated: false,
    };
    appendCommodity(newCommodity);
  };

  const handleFileChange = (
    fieldName: keyof SubmissionFormValues["files"],
    file: File | null
  ) => {
    if (file) {
      // Validate and sanitize filename for Azure compatibility
      const validation = validateAzureFilename(file.name);
      const sanitizedFilename = sanitizeFilenameForAzure(file.name);

      // Show toast notification if filename was modified
      if (!validation.isValid) {
        toast({
          title: "File renamed for compatibility",
          description: `"${file.name}" was renamed to "${sanitizedFilename}" to ensure storage compatibility.`,
          variant: "default",
        });
      }

      // Create file with sanitized name
      const sanitizedFile = createFileWithSanitizedName(
        file,
        sanitizedFilename
      );

      // Handle multiple files for sprinklerInfo, architecturalFile, and floorPlanElevations
      if (
        fieldName === "sprinklerInfo" ||
        fieldName === "architecturalFile" ||
        fieldName === "floorPlanElevations"
      ) {
        const currentFiles =
          (form.getValues(`files.${fieldName}`) as File[]) || [];
        const updatedFiles = [...currentFiles, sanitizedFile];
        form.setValue(`files.${fieldName}`, updatedFiles, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        form.setValue(`files.${fieldName}`, sanitizedFile, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } else {
      form.setValue(`files.${fieldName}`, file, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  // New function to handle multiple file removal
  const handleMultipleFileRemove = (
    fieldName: "sprinklerInfo" | "architecturalFile" | "floorPlanElevations",
    fileIndex: number
  ) => {
    const currentFiles = (form.getValues(`files.${fieldName}`) as File[]) || [];
    const updatedFiles = currentFiles.filter((_, index) => index !== fileIndex);
    form.setValue(`files.${fieldName}`, updatedFiles, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const createSubmissionMutation = useMutation({
    mutationFn: (dto: CreateSubmissionRequestDto) =>
      projectApi.createSubmission(projectId, dto),
    onSuccess: (response) => {
      clearDraftFromBackend(); // Clear draft from backend on successful submission
      toast({
        title: "Submission Successful",
        description: response.message,
      });
      navigate(`/projects/${projectId}`);
      queryClient.invalidateQueries({ queryKey: ["submissions", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => {
      toast({
        title: "Submission Error",
        description: "Failed to submit form. Please try again.",
        variant: "destructive",
      });
      console.error("Submission error:", error);
    },
  });

  const updateSubmissionMutation = useMutation({
    mutationFn: (dto: CreateSubmissionRequestDto) =>
      projectApi.updateSubmission(projectId, dto),
    onSuccess: (response) => {
      clearDraftFromBackend(); // Clear draft from backend on successful update
      toast({
        title: "Submission updated Successfully",
        description: response.message,
      });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["submissions", projectId] });
      navigate(`/projects/${projectId}`);
    },
    onError: (error) => {
      toast({
        title: "Submission Error",
        description: "Failed to submit form. Please try again.",
        variant: "destructive",
      });
      console.error("Submission error:", error);
    },
  });

  const onSubmit = async (data: SubmissionFormValues) => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to submit a form",
        variant: "destructive",
      });
      return;
    }

    // Get existing files from backend (if in edit mode)
    const existingFiles = projectDetails?.submission?.files;

    // Helper function to scroll to first error field
    const scrollToFirstError = (el) => {
      setTimeout(() => {
        const firstErrorField = document.getElementById(el);

        if (firstErrorField) {
          firstErrorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    };

    // Manual validation for files
    // Check if PO file exists in: existing submission (not removed), draft files (not removed), or new upload
    const hasPO =
      (existingFiles?.po && !removedFiles.has("po")) ||
      (draftFiles?.po &&
        !Array.isArray(draftFiles.po) &&
        !removedFiles.has("po")) ||
      data.files.po;
    if (!hasPO) {
      form.setError("files.po", {
        type: "manual",
        message: "PO Document file is required",
      });
      scrollToFirstError("filespo");
      return;
    }

    // Check if BOM file exists in: existing submission (not removed), draft files (not removed), or new upload
    const hasBOM =
      (existingFiles?.bom && !removedFiles.has("bom")) ||
      (draftFiles?.bom &&
        !Array.isArray(draftFiles.bom) &&
        !removedFiles.has("bom")) ||
      data.files.bom;
    if (!hasBOM) {
      form.setError("files.bom", {
        type: "manual",
        message: "Prelim or BOM & manufacturer info file is required",
      });
      scrollToFirstError("filesbom");
      return;
    }

    // Check if floor plan elevations exist in: existing submission (not removed), draft files (not removed), or new upload
    const hasExistingFloorPlan =
      existingFiles?.floorPlanElevations &&
      (Array.isArray(existingFiles.floorPlanElevations)
        ? existingFiles.floorPlanElevations.filter(
            (f) => !removedExistingFiles.has(f.blobName || f.name)
          ).length > 0
        : !removedFiles.has("floorPlanElevations"));
    const hasDraftFloorPlan =
      draftFiles?.floorPlanElevations &&
      !removedFiles.has("floorPlanElevations") &&
      (Array.isArray(draftFiles.floorPlanElevations)
        ? draftFiles.floorPlanElevations.length > 0
        : true);
    const hasNewFloorPlan = (data.files.floorPlanElevations?.length || 0) > 0;

    if (!hasExistingFloorPlan && !hasDraftFloorPlan && !hasNewFloorPlan) {
      form.setError("files.floorPlanElevations", {
        type: "manual",
        message: "Floor plan elevations files are required",
      });
      scrollToFirstError("filesfloorPlanElevations");
      return;
    }

    // Validate commodity letter or commodities
    if (data.enterCommodity) {
      if (data.commodities.length === 0) {
        form.setError("enterCommodity", {
          type: "manual",
          message: "Add at least one commodity",
        });
        scrollToFirstError("enterCommodity");
        return;
      }
    } else {
      // Check if commodity letter exists in: existing submission (not removed), draft files (not removed), or new upload
      const hasCommodityLetter =
        (existingFiles?.commodityLetter &&
          !removedFiles.has("commodityLetter")) ||
        (draftFiles?.commodityLetter &&
          !Array.isArray(draftFiles.commodityLetter) &&
          !removedFiles.has("commodityLetter")) ||
        data.files.commodityLetter;
      if (!hasCommodityLetter) {
        form.setError("files.commodityLetter", {
          type: "manual",
          message:
            "Either upload a commodity letter or add at least one commodity",
        });
        scrollToFirstError("filescommodityLetter");
        return;
      }
    }

    // Validate sprinkler info - check if existing files (not removed), draft files (not removed), or new files are provided
    const hasExistingSprinklerInfo =
      existingFiles?.sprinklerInfo &&
      (Array.isArray(existingFiles.sprinklerInfo)
        ? existingFiles.sprinklerInfo.filter(
            (f) => !removedExistingFiles.has(f.blobName || f.name)
          ).length > 0
        : !removedFiles.has("sprinklerInfo"));
    const hasDraftSprinklerInfo =
      draftFiles?.sprinklerInfo &&
      !removedFiles.has("sprinklerInfo") &&
      (Array.isArray(draftFiles.sprinklerInfo)
        ? draftFiles.sprinklerInfo.length > 0
        : true);
    const hasNewSprinklerInfo =
      data.files.sprinklerInfo &&
      (Array.isArray(data.files.sprinklerInfo)
        ? data.files.sprinklerInfo.length > 0
        : true);

    if (
      !hasExistingSprinklerInfo &&
      !hasDraftSprinklerInfo &&
      !hasNewSprinklerInfo
    ) {
      form.setError("files.sprinklerInfo", {
        type: "manual",
        message: "Sprinkler system information file is required",
      });
      scrollToFirstError("filessprinklerInfo");
      return;
    }

    // Validate contractor license when uploadLicense is checked
    if (data.uploadLicense) {
      // Check if contractor license exists in: existing submission (not removed), draft files (not removed), or new upload
      const hasContractorLicense =
        (existingFiles?.contractorLicense &&
          !removedFiles.has("contractorLicense")) ||
        (draftFiles?.contractorLicense &&
          !Array.isArray(draftFiles.contractorLicense) &&
          !removedFiles.has("contractorLicense")) ||
        data.files.contractorLicense;
      if (!hasContractorLicense) {
        form.setError("files.contractorLicense", {
          type: "manual",
          message: "Contractor license file is required",
        });
        scrollToFirstError("filescontractorLicense");
        return;
      }
    }

    // Prepare DTO
    const { files, ...formData } = data;

    // Prepare remainingFiles data for update operations
    const remainingFilesData: Record<string, string[]> = {};
    if (projectDetails?.submission?.files) {
      const existingFiles = projectDetails.submission.files;

      // For each array file type, determine which files should remain
      ["sprinklerInfo", "architecturalFile", "floorPlanElevations"].forEach(
        (fieldName) => {
          const existingFieldFiles = existingFiles[fieldName];
          if (Array.isArray(existingFieldFiles)) {
            // Filter out files that were marked for removal
            const filesToKeep = existingFieldFiles
              .filter(
                (file) => !removedExistingFiles.has(file.blobName || file.name)
              )
              .map(
                (file) => file.blobName || file._id?.toString() || file.name
              );

            remainingFilesData[fieldName] = filesToKeep;
          } else if (existingFieldFiles && !removedFiles.has(fieldName)) {
            // For single files that still exist and weren't removed
            remainingFilesData[fieldName] = [
              existingFieldFiles.blobName ||
                existingFieldFiles._id?.toString() ||
                existingFieldFiles.name,
            ];
          } else {
            // No files to keep
            remainingFilesData[fieldName] = [];
          }
        }
      );
    }

    const dto: CreateSubmissionRequestDto = {
      ...(formData as CreateSubmissionRequestDto),
      projectId,
      files: files as Record<string, File | File[]>,
      ...(Object.keys(remainingFilesData).length > 0 && {
        remainingFiles: remainingFilesData,
      }),
    };

    if (projectDetails) {
      updateSubmissionMutation.mutate(dto);
    } else {
      createSubmissionMutation.mutate(dto);
    }
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit, () => {
        const firstErrorField = document.querySelector('[aria-invalid="true"]');
        if (firstErrorField) {
          (firstErrorField as HTMLElement).scrollIntoView({
            behavior: "smooth",
          });
        }
      })}
      className="space-y-8"
    >
      {/* Autosave indicator for new submissions */}
      {isNewSubmission && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center text-sm text-blue-700">
            <div
              className={`w-2 h-2 rounded-full mr-2 ${
                isSaving
                  ? "bg-yellow-500 animate-pulse"
                  : "bg-blue-500 animate-pulse"
              }`}
            ></div>
            <span>
              {isSaving
                ? "Saving draft..."
                : "Autosave enabled - Your progress is automatically saved"}
            </span>
            {lastSaveTime && !isSaving && (
              <span className="ml-3 text-xs text-blue-600">
                Last saved: {lastSaveTime.toLocaleTimeString()}
              </span>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={clearDraft}
            disabled={isSaving}
          >
            Clear Draft
          </Button>
        </div>
      )}

      <Accordion
        type="multiple"
        defaultValue={[
          "section1",
          "section2",
          "section3",
          "section4",
          "section5",
          "section6",
          "section7",
        ]}
        className="w-full"
      >
        {/* Section 1: Project Valuation & PO Details */}
        <AccordionItem value="section1">
          <AccordionTrigger className="text-lg font-semibold">
            1. Project Valuation &amp; PO Details
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="projectValuation">
                  Project Valuation <span className="text-red-500 ml-1">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    $
                  </span>
                  <Input
                    id="projectValuation"
                    placeholder="0.00"
                    className="pl-7"
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    {...form.register("projectValuation", {
                      valueAsNumber: true,
                    })}
                  />
                </div>
                {form.formState.errors.projectValuation && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.projectValuation.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="valuation-upload">
                  Project Valuation Documents (Optional)
                </Label>

                <FileUploadZone
                  id="file.projectValuationDocs"
                  title="Upload Project Valuation Documents"
                  description="PDF, DOCX files"
                  acceptedFormats="PDF, DOCX"
                  accept=".pdf,.docx"
                  disabled={projectDetails && !isAdmin}
                  onBrowseClick={() =>
                    document.getElementById("valuation-upload")?.click()
                  }
                  disabledMessage="Only admins can change files"
                />

                <input
                  id="valuation-upload"
                  type="file"
                  hidden
                  accept=".pdf,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    if (file) handleFileChange("projectValuationDocs", file);
                  }}
                />

                <input
                  type="text"
                  {...form.register("files.projectValuationDocs")}
                  style={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                    height: 0,
                  }}
                  tabIndex={-1}
                />

                <FileErrorMessage
                  message={
                    form.formState.errors.files?.projectValuationDocs?.message
                  }
                />

                {/* Show existing file only */}
                {(projectDetails?.submission?.files?.projectValuationDocs ||
                  (draftFiles?.projectValuationDocs &&
                    !Array.isArray(draftFiles.projectValuationDocs))) &&
                  !projectValuationFile &&
                  !removedFiles.has("projectValuationDocs") && (
                    <ExistingFileDisplay
                      fileName={
                        projectDetails?.submission?.files?.projectValuationDocs
                          ?.name ||
                        (!Array.isArray(draftFiles?.projectValuationDocs) &&
                          draftFiles?.projectValuationDocs?.name) ||
                        ""
                      }
                      fileUrl={
                        projectDetails?.submission?.files?.projectValuationDocs
                          ?.url ||
                        (!Array.isArray(draftFiles?.projectValuationDocs) &&
                          draftFiles?.projectValuationDocs?.url)
                      }
                      isDraft={
                        !!(
                          !projectDetails?.submission?.files
                            ?.projectValuationDocs &&
                          draftFiles?.projectValuationDocs
                        )
                      }
                      showView={true}
                      showDelete={shouldShowDeleteButton(
                        "projectValuationDocs",
                        false,
                        true
                      )}
                      onDelete={() =>
                        handleRemoveExistingFile("projectValuationDocs")
                      }
                    />
                  )}

                <RemovedFileMessage
                  show={
                    !!(
                      (projectDetails?.submission?.files
                        ?.projectValuationDocs ||
                        (draftFiles?.projectValuationDocs &&
                          !Array.isArray(draftFiles.projectValuationDocs))) &&
                      removedFiles.has("projectValuationDocs")
                    )
                  }
                />

                {/* Show both when replacing */}
                {(projectDetails?.submission?.files?.projectValuationDocs ||
                  (draftFiles?.projectValuationDocs &&
                    !Array.isArray(draftFiles.projectValuationDocs))) &&
                  projectValuationFile &&
                  !removedFiles.has("projectValuationDocs") && (
                    <div className="flex flex-col gap-1 mt-2">
                      <div className="flex items-center justify-between hover:bg-primary-foreground p-1 rounded-sm">
                        <span className="text-sm text-blue-600 line-through">
                          {projectDetails?.submission?.files
                            ?.projectValuationDocs?.name ||
                            (!Array.isArray(draftFiles?.projectValuationDocs) &&
                              draftFiles?.projectValuationDocs?.name)}{" "}
                          (will be replaced)
                        </span>
                        {(projectDetails?.submission?.files
                          ?.projectValuationDocs?.url ||
                          (!Array.isArray(draftFiles?.projectValuationDocs) &&
                            draftFiles?.projectValuationDocs?.url)) && (
                          <a
                            href={
                              projectDetails?.submission?.files
                                ?.projectValuationDocs?.url ||
                              (!Array.isArray(
                                draftFiles?.projectValuationDocs
                              ) &&
                                draftFiles?.projectValuationDocs?.url) ||
                              "#"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center ml-2 gap-1 text-xs text-blue-500 underline"
                          >
                            <EyeIcon className="h-4 w-4" /> View File
                          </a>
                        )}
                      </div>
                      <SingleFileDisplay
                        file={projectValuationFile}
                        fileName={projectValuationFile.name}
                        fileSize={projectValuationFile.size / 1024 / 1024}
                        showDelete={shouldShowDeleteButton(
                          "projectValuationDocs",
                          true,
                          true
                        )}
                        onDelete={() =>
                          handleFileChange("projectValuationDocs", null)
                        }
                      />
                    </div>
                  )}

                {/* Show only new */}
                {((!projectDetails?.submission?.files?.projectValuationDocs &&
                  (!draftFiles?.projectValuationDocs ||
                    Array.isArray(draftFiles.projectValuationDocs))) ||
                  removedFiles.has("projectValuationDocs")) &&
                  projectValuationFile && (
                    <SingleFileDisplay
                      file={projectValuationFile}
                      fileName={projectValuationFile.name}
                      fileSize={projectValuationFile.size / 1024 / 1024}
                      showDelete={shouldShowDeleteButton(
                        "projectValuationDocs",
                        true,
                        false
                      )}
                      onDelete={() =>
                        handleFileChange("projectValuationDocs", null)
                      }
                    />
                  )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="poNumber">
                  PO Number <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="poNumber"
                  placeholder="e.g., PO-12345"
                  {...form.register("poNumber")}
                />

                {form.formState.errors.poNumber && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.poNumber.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="files.po">
                  PO Document <span className="text-red-500 ml-1">*</span>
                </Label>

                <FileUploadZone
                  id="filespo"
                  title="Upload PO Document"
                  description="PDF, DOCX files"
                  acceptedFormats="PDF, DOCX"
                  accept=".pdf,.docx"
                  disabled={projectDetails && !isAdmin}
                  onBrowseClick={() =>
                    document.getElementById("files.po")?.click()
                  }
                  disabledMessage="Only admins can change files"
                />

                <input
                  id="files.po"
                  type="file"
                  hidden
                  accept=".pdf,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    handleFileChange("po", file);
                  }}
                />

                <input
                  type="text"
                  {...form.register("files.po")}
                  style={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                    height: 0,
                  }}
                  tabIndex={-1}
                />

                <FileErrorMessage
                  message={form.formState.errors.files?.po?.message}
                />

                {/* Show existing file only */}
                {(projectDetails?.submission?.files?.po ||
                  (draftFiles?.po && !Array.isArray(draftFiles.po))) &&
                  !poFile &&
                  !removedFiles.has("po") && (
                    <ExistingFileDisplay
                      fileName={
                        projectDetails?.submission?.files?.po?.name ||
                        (!Array.isArray(draftFiles?.po) &&
                          draftFiles?.po?.name) ||
                        ""
                      }
                      fileUrl={
                        projectDetails?.submission?.files?.po?.url ||
                        (!Array.isArray(draftFiles?.po) && draftFiles?.po?.url)
                      }
                      isDraft={
                        !!(
                          !projectDetails?.submission?.files?.po &&
                          draftFiles?.po
                        )
                      }
                      showView={true}
                      showDelete={shouldShowDeleteButton("po", false, true)}
                      onDelete={() => handleRemoveExistingFile("po")}
                    />
                  )}

                <RemovedFileMessage
                  show={
                    !!(
                      (projectDetails?.submission?.files?.po ||
                        (draftFiles?.po && !Array.isArray(draftFiles.po))) &&
                      removedFiles.has("po")
                    )
                  }
                />

                {/* Show both when replacing */}
                {(projectDetails?.submission?.files?.po ||
                  (draftFiles?.po && !Array.isArray(draftFiles.po))) &&
                  poFile &&
                  !removedFiles.has("po") && (
                    <div className="flex flex-col gap-1 mt-2">
                      <div className="flex items-center justify-between hover:bg-primary-foreground p-1 rounded-sm">
                        <span className="text-sm text-blue-600 line-through">
                          {projectDetails?.submission?.files?.po?.name ||
                            (!Array.isArray(draftFiles?.po) &&
                              draftFiles?.po?.name)}{" "}
                          (will be replaced)
                        </span>
                        {(projectDetails?.submission?.files?.po?.url ||
                          (!Array.isArray(draftFiles?.po) &&
                            draftFiles?.po?.url)) && (
                          <a
                            href={
                              projectDetails?.submission?.files?.po?.url ||
                              (!Array.isArray(draftFiles?.po) &&
                                draftFiles?.po?.url) ||
                              "#"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center ml-2 gap-1 text-xs text-blue-500 underline"
                          >
                            <EyeIcon className="h-4 w-4" /> View File
                          </a>
                        )}
                      </div>
                      <SingleFileDisplay
                        file={poFile}
                        fileName={poFile.name}
                        fileSize={poFile.size / 1024 / 1024}
                        showDelete={shouldShowDeleteButton("po", true, true)}
                        onDelete={() => handleFileChange("po", null)}
                      />
                    </div>
                  )}

                {/* Show only new */}
                {((!projectDetails?.submission?.files?.po &&
                  (!draftFiles?.po || Array.isArray(draftFiles.po))) ||
                  removedFiles.has("po")) &&
                  poFile && (
                    <SingleFileDisplay
                      file={poFile}
                      fileName={poFile.name}
                      fileSize={poFile.size / 1024 / 1024}
                      showDelete={shouldShowDeleteButton("po", true, false)}
                      onDelete={() => handleFileChange("po", null)}
                    />
                  )}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 2: Site & Safety Assessments */}
        <AccordionItem value="section1">
          <AccordionTrigger className="text-lg font-semibold">
            2. Site &amp; Safety Assessments
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <div className="space-y-2">
              <Label>
                Is this a fire‑hazard zone?{" "}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <RadioGroup
                value={watchIsFireHazardZone ? "true" : "false"}
                onValueChange={(value) =>
                  form.setValue("isFireHazardZone", value === "true")
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="fire-no" />
                  <Label htmlFor="fire-no">No</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="fire-yes" />
                  <Label htmlFor="fire-yes">Yes</Label>
                </div>
              </RadioGroup>
            </div>

            {watchIsFireHazardZone && (
              <div className="space-y-2 border-l-4 border-yellow-500 pl-4">
                <div className="flex items-center text-yellow-500 mb-2">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  <span className="font-medium">
                    Additional information required
                  </span>
                </div>
                <Label htmlFor="fireHazardDetails">
                  Describe specific hazard and mitigation measures
                </Label>
                <Textarea
                  id="fireHazardDetails"
                  placeholder="Provide details about the fire hazard and your mitigation measures..."
                  {...form.register("fireHazardDetails")}
                />
                {form.formState.errors.fireHazardDetails && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.fireHazardDetails.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>
                Will warehouse be publicly accessible?{" "}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <RadioGroup
                value={watchIsPubliclyAccessible ? "true" : "false"}
                onValueChange={(value) =>
                  form.setValue("isPubliclyAccessible", value === "true")
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="public-no" />
                  <Label htmlFor="public-no">No</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="public-yes" />
                  <Label htmlFor="public-yes">Yes</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>
                Liquid propane tank storage inside warehouse?{" "}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <RadioGroup
                value={watchHasLiquidPropaneTankStorage ? "true" : "false"}
                onValueChange={(value) =>
                  form.setValue("hasLiquidPropaneTankStorage", value === "true")
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="propane-no" />
                  <Label htmlFor="propane-no">No</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="propane-yes" />
                  <Label htmlFor="propane-yes">Yes</Label>
                </div>
              </RadioGroup>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 3: Commodity Details */}
        <AccordionItem value="section2">
          <AccordionTrigger className="text-lg font-semibold">
            3. Commodity Details
          </AccordionTrigger>
          <AccordionContent className="space-y-6">
            {/* Commodity Letter Upload First */}
            <div className="space-y-2">
              <Label htmlFor="commodityLetterFile">
                Upload Commodity Letter
              </Label>

              <FileUploadZone
                id="filescommodityLetter"
                title="Upload Commodity Letter"
                description="PDF, DOC, DOCX files"
                acceptedFormats="PDF, DOC, DOCX"
                accept=".pdf,.doc,.docx"
                disabled={projectDetails && !isAdmin}
                onBrowseClick={() =>
                  document.getElementById("commodityLetterFile")?.click()
                }
                disabledMessage="Only admins can change files"
              />

              <input
                id="commodityLetterFile"
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  handleFileChange("commodityLetter", file);
                }}
              />

              <input
                type="text"
                {...form.register("files.commodityLetter")}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  height: 0,
                }}
                tabIndex={-1}
              />

              <FileErrorMessage
                message={form.formState.errors.files?.commodityLetter?.message}
              />

              {/* Show existing file (submission or draft) */}
              {(projectDetails?.submission?.files?.commodityLetter ||
                (draftFiles?.commodityLetter &&
                  !Array.isArray(draftFiles.commodityLetter))) &&
                !commodityLetterFile &&
                !removedFiles.has("commodityLetter") && (
                  <ExistingFileDisplay
                    fileName={
                      projectDetails?.submission?.files?.commodityLetter
                        ?.name ||
                      (!Array.isArray(draftFiles?.commodityLetter) &&
                        draftFiles?.commodityLetter?.name) ||
                      ""
                    }
                    fileUrl={
                      projectDetails?.submission?.files?.commodityLetter?.url ||
                      (!Array.isArray(draftFiles?.commodityLetter) &&
                        draftFiles?.commodityLetter?.url)
                    }
                    isDraft={
                      !!(
                        !projectDetails?.submission?.files?.commodityLetter &&
                        draftFiles?.commodityLetter
                      )
                    }
                    showView={true}
                    showDelete={shouldShowDeleteButton(
                      "commodityLetter",
                      false,
                      true
                    )}
                    onDelete={() => handleRemoveExistingFile("commodityLetter")}
                  />
                )}

              {/* Show removed message */}
              <RemovedFileMessage
                show={
                  !!(
                    (projectDetails?.submission?.files?.commodityLetter ||
                      (draftFiles?.commodityLetter &&
                        !Array.isArray(draftFiles.commodityLetter))) &&
                    removedFiles.has("commodityLetter")
                  )
                }
              />

              {/* Show both old and new files when replacing */}
              {(projectDetails?.submission?.files?.commodityLetter ||
                (draftFiles?.commodityLetter &&
                  !Array.isArray(draftFiles.commodityLetter))) &&
                commodityLetterFile &&
                !removedFiles.has("commodityLetter") && (
                  <div className="flex flex-col gap-1 mt-2">
                    <div className="flex items-center justify-between hover:bg-primary-foreground p-1 rounded-sm">
                      <span className="text-sm text-blue-600 line-through">
                        {projectDetails?.submission?.files?.commodityLetter
                          ?.name ||
                          (!Array.isArray(draftFiles?.commodityLetter) &&
                            draftFiles?.commodityLetter?.name)}{" "}
                        (will be replaced)
                      </span>
                      {(projectDetails?.submission?.files?.commodityLetter
                        ?.url ||
                        (!Array.isArray(draftFiles?.commodityLetter) &&
                          draftFiles?.commodityLetter?.url)) && (
                        <a
                          href={
                            projectDetails?.submission?.files?.commodityLetter
                              ?.url ||
                            (!Array.isArray(draftFiles?.commodityLetter) &&
                              draftFiles?.commodityLetter?.url) ||
                            "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center ml-2 gap-1 text-xs text-blue-500 underline"
                        >
                          <EyeIcon className="h-4 w-4" /> View File
                        </a>
                      )}
                    </div>
                    <SingleFileDisplay
                      file={commodityLetterFile}
                      fileName={commodityLetterFile.name}
                      fileSize={commodityLetterFile.size / 1024 / 1024}
                      showDelete={shouldShowDeleteButton(
                        "commodityLetter",
                        true,
                        true
                      )}
                      onDelete={() => handleFileChange("commodityLetter", null)}
                    />
                  </div>
                )}

              {/* Show only new file */}
              {((!projectDetails?.submission?.files?.commodityLetter &&
                (!draftFiles?.commodityLetter ||
                  Array.isArray(draftFiles?.commodityLetter))) ||
                removedFiles.has("commodityLetter")) &&
                commodityLetterFile && (
                  <SingleFileDisplay
                    file={commodityLetterFile}
                    fileName={commodityLetterFile.name}
                    fileSize={commodityLetterFile.size / 1024 / 1024}
                    showDelete={shouldShowDeleteButton(
                      "commodityLetter",
                      true,
                      false
                    )}
                    onDelete={() => handleFileChange("commodityLetter", null)}
                  />
                )}
            </div>

            {commodityLetterFile === null && (
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="enterCommodity"
                      checked={form.watch("enterCommodity")}
                      onCheckedChange={(checked) =>
                        form.setValue("enterCommodity", !!checked)
                      }
                      {...form.register("enterCommodity")}
                    />
                    <Label htmlFor="enterCommodity">
                      I don't have a commodity letter; I want to add details
                      manually.
                    </Label>
                  </div>
                  {form.formState.errors.enterCommodity && (
                    <p className="text-sm text-red-600 mt-2">
                      {form.formState.errors.enterCommodity.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {enterCommodity && (
              <>
                <div className="flex justify-end items-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCommodity}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Commodity
                  </Button>
                </div>
                {commodities.length === 0 ? (
                  <div className="text-center py-6 border border-dashed rounded-md">
                    <p className="text-muted-foreground">
                      No commodities added yet. Click "Add Commodity" to begin.
                    </p>
                  </div>
                ) : (
                  commodities.map((c, i) => (
                    <CommodityCard
                      key={c.id}
                      index={i}
                      form={form}
                      removeCommodity={removeCommodity}
                      packagingMaterials={packagingMaterials}
                      packagingTypes={packagingTypes}
                      plasticTypes={plasticTypes}
                    />
                  ))
                )}
                {form.formState.errors.commodities && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.commodities.message as string}
                  </p>
                )}
              </>
            )}

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasHazardousMaterials"
                  checked={form.watch("hasHazardousMaterials")}
                  onCheckedChange={(checked) =>
                    form.setValue("hasHazardousMaterials", !!checked)
                  }
                />
                <Label htmlFor="hasHazardousMaterials">
                  Does this project involve hazardous materials?
                </Label>
              </div>

              {watchHasHazardousMaterials && (
                <div className="space-y-2 ml-6">
                  <Label htmlFor="hazardousMaterialsDetails">
                    Hazardous Materials Details{" "}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Textarea
                    id="hazardousMaterialsDetails"
                    placeholder="Describe the hazardous materials and safety measures..."
                    {...form.register("hazardousMaterialsDetails")}
                  />
                  {form.formState.errors.hazardousMaterialsDetails && (
                    <p className="text-red-500 text-sm">
                      {form.formState.errors.hazardousMaterialsDetails.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 4: Storage Configuration */}
        <AccordionItem value="section3">
          <AccordionTrigger className="text-lg font-semibold">
            4. Storage Configuration
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <div className="space-y-2">
              <Label>
                Storage Type <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {storageTypes.map((type) => (
                  <div className="flex items-center space-x-2" key={type.value}>
                    <Checkbox
                      id={`storageType-${type.value}`}
                      checked={watchStorageTypes?.includes(
                        type.value as StorageType
                      )}
                      onCheckedChange={(checked) => {
                        const types = [...(form.watch("storageTypes") || [])];
                        if (checked) {
                          types.push(type.value as StorageType);
                        } else {
                          const idx = types.indexOf(type.value as StorageType);
                          if (idx !== -1) types.splice(idx, 1);
                        }
                        form.setValue("storageTypes", types, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                    <Label htmlFor={`storageType-${type.value}`}>
                      {type.label}
                    </Label>
                  </div>
                ))}
              </div>

              <input
                type="text"
                {...form.register("storageTypes")}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  height: 0,
                }}
                tabIndex={-1}
              />
              {form.formState.errors.storageTypes && (
                <p className="text-red-500 text-sm">
                  {form.formState.errors.storageTypes.message as string}
                </p>
              )}
            </div>

            {watchStorageTypes?.includes("other") && (
              <div className="space-y-2 ml-6">
                <Label htmlFor="otherStorageTypeDetails">
                  Other Storage Type Details{" "}
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="otherStorageTypeDetails"
                  placeholder="Describe the other storage type"
                  {...form.register("otherStorageTypeDetails")}
                />
                {form.formState.errors.otherStorageTypeDetails && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.otherStorageTypeDetails.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="palletDimensions">
                Pallet Dimensions (inches){" "}
              </Label>
              <Input
                id="palletDimensions"
                placeholder='e.g., 48" x 40" x 60"'
                {...form.register("palletDimensions")}
              />
              {form.formState.errors.palletDimensions && (
                <p className="text-red-500 text-sm">
                  {form.formState.errors.palletDimensions.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasOversizedLoads"
                  checked={watchHasOversizedLoads}
                  onCheckedChange={(checked) =>
                    form.setValue("hasOversizedLoads", !!checked)
                  }
                />
                <Label htmlFor="hasOversizedLoads">
                  Will there be oversized loads?
                </Label>
              </div>

              {watchHasOversizedLoads && (
                <div className="space-y-2 ml-6">
                  <Label htmlFor="oversizedLoadsDetails">
                    Oversized Loads Details{" "}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Textarea
                    id="oversizedLoadsDetails"
                    placeholder="Describe the oversized loads..."
                    {...form.register("oversizedLoadsDetails")}
                  />
                  {form.formState.errors.oversizedLoadsDetails && (
                    <p className="text-red-500 text-sm">
                      {form.formState.errors.oversizedLoadsDetails.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Floor Plan & Elevations section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="floorPlanElevationsFile">
                  Floor Plan & Elevations{" "}
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <p className="text-sm text-muted-foreground">
                  Upload floor plans and elevation drawings (PDFs or DWG files)
                </p>

                <FileUploadZone
                  id="filesfloorPlanElevations"
                  title="Upload Floor Plan & Elevation Files"
                  description="PDF, DWG, DXF (MAX. 50MB per file) - Multiple files allowed"
                  acceptedFormats="PDF, DWG, DXF"
                  accept=".pdf,.dwg,.dxf"
                  multiple={true}
                  disabled={projectDetails && !isAdmin}
                  onBrowseClick={() =>
                    document.getElementById("floorPlanElevationsFile")?.click()
                  }
                />

                <input
                  id="floorPlanElevationsFile"
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.dwg,.dxf"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach((file) =>
                      handleFileChange("floorPlanElevations", file)
                    );
                    e.target.value = "";
                  }}
                />

                <AdminOnlyWarning show={!!(projectDetails && !isAdmin)} />

                {/* Display newly selected files */}
                <MultipleFilesList
                  files={floorPlanElevationsFile || []}
                  title="Uploaded Files"
                  showDelete={!projectDetails || isAdmin}
                  onDeleteFile={(index) =>
                    handleMultipleFileRemove("floorPlanElevations", index)
                  }
                />

                {/* Display existing files from server */}
                {((projectDetails?.submission?.files?.floorPlanElevations &&
                  projectDetails.submission.files.floorPlanElevations.length >
                    0) ||
                  (draftFiles?.floorPlanElevations &&
                    Array.isArray(draftFiles.floorPlanElevations) &&
                    draftFiles.floorPlanElevations.length > 0)) && (
                  <ExistingFilesList
                    files={[
                      ...(projectDetails?.submission?.files?.floorPlanElevations
                        ?.filter(
                          (file) =>
                            !removedExistingFiles.has(
                              file.blobName || file.name
                            )
                        )
                        .map((file) => ({
                          name: file.name,
                          url: file.url,
                          blobName: file.blobName || file.name,
                          type: file.type,
                          size: file.size,
                        })) || []),
                      ...(!projectDetails?.submission?.files
                        ?.floorPlanElevations &&
                      draftFiles?.floorPlanElevations &&
                      Array.isArray(draftFiles.floorPlanElevations)
                        ? draftFiles.floorPlanElevations
                        : []),
                    ]}
                    title="Existing Files"
                    isDraft={
                      !!(
                        !projectDetails?.submission?.files
                          ?.floorPlanElevations &&
                        draftFiles?.floorPlanElevations
                      )
                    }
                    showView={true}
                    showDelete={isAdmin || !projectDetails?.submission}
                    onDeleteFile={(blobName) =>
                      projectDetails?.submission
                        ? handleRemoveExistingArrayFile(blobName)
                        : handleRemoveExistingFile("floorPlanElevations")
                    }
                  />
                )}

                <FileErrorMessage
                  message={
                    form.formState.errors.files?.floorPlanElevations?.message
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Pallet Material <span className="text-red-500 ml-1">*</span>
              </Label>
              <RadioGroup
                value={form.watch("palletMaterial")}
                onValueChange={(value) =>
                  form.setValue("palletMaterial", value as PalletMaterial)
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="wood" id="pallet-wood" />
                  <Label htmlFor="pallet-wood">Wood</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="plastic" id="pallet-plastic" />
                  <Label htmlFor="pallet-plastic">Plastic</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>Rack Accessories</Label>
              <div className="grid grid-cols-2 gap-2">
                {rackAccessories.map((accessory) => (
                  <div
                    className="flex items-center space-x-2"
                    key={accessory.value}
                  >
                    <Checkbox
                      id={`rack-${accessory.value}`}
                      checked={form
                        .watch("rackAccessories")
                        ?.includes(accessory.value as RackAccessory)}
                      onCheckedChange={(checked) => {
                        const accessories = [
                          ...(form.watch("rackAccessories") || []),
                        ];
                        if (checked) {
                          accessories.push(accessory.value as RackAccessory);
                        } else {
                          const idx = accessories.indexOf(
                            accessory.value as RackAccessory
                          );
                          if (idx !== -1) accessories.splice(idx, 1);
                        }
                        form.setValue("rackAccessories", accessories);
                      }}
                    />
                    <Label htmlFor={`rack-${accessory.value}`}>
                      {accessory.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 5: Handling & Operations */}
        <AccordionItem value="section4">
          <AccordionTrigger className="text-lg font-semibold">
            5. Handling & Operations
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <div className="space-y-2">
              <Label>
                Will pallet loads be hand-stacked or mechanically stored?
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <RadioGroup
                value={watchStorageMethod}
                onValueChange={(value) =>
                  form.setValue("storageMethod", value as StorageMethod)
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="hand-stacked" id="hand-stacked" />
                  <Label htmlFor="hand-stacked">Hand-stacked</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="mechanical" id="mechanical" />
                  <Label htmlFor="mechanical">Mechanically</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>
                Idle (unused) pallet storage inside warehouse?{" "}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <RadioGroup
                value={watchHasIdlePalletStorage ? "true" : "false"}
                onValueChange={(value) =>
                  form.setValue("hasIdlePalletStorage", value === "true")
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="idle-no" />
                  <Label htmlFor="idle-no">No</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="idle-yes" />
                  <Label htmlFor="idle-yes">Yes</Label>
                </div>
              </RadioGroup>
            </div>

            {watchHasIdlePalletStorage && (
              <div className="space-y-2 ml-6">
                <Label>
                  Pallet material <span className="text-red-500 ml-1">*</span>
                </Label>
                <RadioGroup
                  value={watchIdlePalletMaterial || "wood"}
                  onValueChange={(value) =>
                    form.setValue("idlePalletMaterial", value as PalletMaterial)
                  }
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="wood" id="idle-wood" />
                    <Label htmlFor="idle-wood">Wood</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="plastic" id="idle-plastic" />
                    <Label htmlFor="idle-plastic">Plastic</Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Section 6: Sprinkler & Fire Protection */}
        <AccordionItem value="section5">
          <AccordionTrigger className="text-lg font-semibold">
            6. Sprinkler &amp; Fire Protection
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="buildingHeight">
                Building Height (feet){" "}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                id="buildingHeight"
                type="number"
                step="any"
                placeholder="e.g., 30"
                {...form.register("buildingHeight", { valueAsNumber: true })}
              />
              {form.formState.errors.buildingHeight && (
                <p className="text-red-500 text-sm">
                  {form.formState.errors.buildingHeight.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>
                Sprinkler System Type{" "}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <RadioGroup
                value={form.watch("sprinklerSystemType")}
                onValueChange={(value) =>
                  form.setValue(
                    "sprinklerSystemType",
                    value as SprinklerSystemType
                  )
                }
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="esfr" id="sprinkler-esfr" />
                  <Label htmlFor="sprinkler-esfr">
                    ESFR (Early Suppression Fast Response)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cmsa" id="sprinkler-cmsa" />
                  <Label htmlFor="sprinkler-cmsa">
                    CMSA (Control Mode Specific Application)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cmda" id="sprinkler-cmda" />
                  <Label htmlFor="sprinkler-cmda">
                    CMDA (Control Mode Density Area)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="not-sure" id="sprinkler-not-sure" />
                  <Label htmlFor="sprinkler-not-sure">Not Sure</Label>
                </div>
              </RadioGroup>
            </div>

            {(watchSprinklerType === "esfr" ||
              watchSprinklerType === "cmsa") && (
              <div className="space-y-2">
                <Label htmlFor="kFactorPsi">
                  K-Factor/PSI <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="kFactorPsi"
                  placeholder="e.g., K14 @ 50 psi"
                  {...form.register("kFactorPsi")}
                />
                {form.formState.errors.kFactorPsi && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.kFactorPsi.message}
                  </p>
                )}
              </div>
            )}

            {watchSprinklerType === "cmda" && (
              <div className="space-y-2">
                <Label htmlFor="densityRequirement">
                  Density Requirement{" "}
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="densityRequirement"
                  placeholder="e.g., 0.45 gpm/sq ft"
                  {...form.register("densityRequirement")}
                />
                {form.formState.errors.densityRequirement && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.densityRequirement.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="sprinklerFile">
                Upload Sprinkler System Information
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Please verify that the sprinkler drawing includes locations of
                the FDCs, sprinkler valves, and risers.
              </p>

              <FileUploadZone
                id="filessprinklerInfo"
                title="Upload Sprinkler System Information"
                description="JPEG, PDF, PNG, DOCX, DWG files - Multiple files allowed (MAX. 10MB per file)"
                acceptedFormats="JPEG, PDF, PNG, DOCX, DWG"
                accept=".pdf,.docx,.jpg,.jpeg,.png,.dwg"
                multiple={true}
                disabled={false}
                onBrowseClick={() =>
                  document.getElementById("sprinklerFile")?.click()
                }
              />

              <input
                id="sprinklerFile"
                type="file"
                hidden
                multiple
                accept=".pdf,.docx,.jpg,.jpeg,.png,.dwg"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach((file) =>
                    handleFileChange("sprinklerInfo", file)
                  );
                  e.target.value = ""; // Reset input for re-selection
                }}
              />

              <input
                type="text"
                {...form.register("files.sprinklerInfo")}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  height: 0,
                }}
                tabIndex={-1}
              />

              <FileErrorMessage
                message={form.formState.errors.files?.sprinklerInfo?.message}
              />

              {/* Show existing submission files */}
              {projectDetails?.submission?.files?.sprinklerInfo &&
                !removedFiles.has("sprinklerInfo") && (
                  <CompactExistingFilesList
                    files={
                      Array.isArray(
                        projectDetails.submission.files.sprinklerInfo
                      )
                        ? projectDetails.submission.files.sprinklerInfo.filter(
                            (file) =>
                              !removedExistingFiles.has(
                                file.blobName || file.name
                              )
                          )
                        : [projectDetails.submission.files.sprinklerInfo]
                    }
                    isDraft={false}
                    showView={true}
                    showDelete={shouldShowDeleteButton(
                      "sprinklerInfo",
                      false,
                      true
                    )}
                    onDeleteFile={(blobName) =>
                      handleRemoveExistingArrayFile(blobName)
                    }
                  />
                )}

              {/* Show draft files */}
              {draftFiles?.sprinklerInfo &&
                Array.isArray(draftFiles.sprinklerInfo) &&
                !projectDetails?.submission?.files?.sprinklerInfo &&
                !removedFiles.has("sprinklerInfo") && (
                  <CompactExistingFilesList
                    files={draftFiles.sprinklerInfo}
                    isDraft={true}
                    showView={true}
                    showDelete={true}
                    onDeleteFile={() =>
                      handleRemoveExistingFile("sprinklerInfo")
                    }
                  />
                )}

              {/* Show new files */}
              {sprinklerInfoFile &&
                Array.isArray(sprinklerInfoFile) &&
                sprinklerInfoFile.length > 0 && (
                  <CompactMultipleFilesList
                    files={sprinklerInfoFile}
                    showDelete={true}
                    onDeleteFile={(index) =>
                      handleMultipleFileRemove("sprinklerInfo", index)
                    }
                  />
                )}

              <RemovedFileMessage
                show={
                  !!(
                    projectDetails?.submission?.files?.sprinklerInfo &&
                    removedFiles.has("sprinklerInfo")
                  )
                }
                message="Files removed - will be deleted on save"
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 7: Engineering & Documents */}
        <AccordionItem value="section7">
          <AccordionTrigger className="text-lg font-semibold">
            7. Engineering & Documents
          </AccordionTrigger>
          <AccordionContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="cad-file-upload">CAD File</Label>

              <FileUploadZone
                id="filescadFile"
                title="Upload CAD File"
                description="DWG, ZIP, PDF file"
                acceptedFormats="DWG, ZIP, PDF"
                accept=".dwg,.zip,.pdf"
                disabled={false}
                onBrowseClick={() =>
                  document.getElementById("cad-file-upload")?.click()
                }
              />

              <input
                id="cad-file-upload"
                type="file"
                hidden
                accept=".dwg,.zip,.pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  if (file) handleFileChange("cadFile", file);
                }}
              />

              <input
                type="text"
                {...form.register("files.cadFile")}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  height: 0,
                }}
                tabIndex={-1}
              />

              <FileErrorMessage
                message={form.formState.errors.files?.cadFile?.message}
              />

              {/* Show existing file only */}
              {(projectDetails?.submission?.files?.cadFile ||
                (draftFiles?.cadFile && !Array.isArray(draftFiles.cadFile))) &&
                !cadFile &&
                !removedFiles.has("cadFile") && (
                  <ExistingFileDisplay
                    fileName={
                      projectDetails?.submission?.files?.cadFile?.name ||
                      (!Array.isArray(draftFiles?.cadFile) &&
                        draftFiles?.cadFile?.name) ||
                      ""
                    }
                    fileUrl={
                      projectDetails?.submission?.files?.cadFile?.url ||
                      (!Array.isArray(draftFiles?.cadFile) &&
                        draftFiles?.cadFile?.url)
                    }
                    isDraft={
                      !!(
                        !projectDetails?.submission?.files?.cadFile &&
                        draftFiles?.cadFile
                      )
                    }
                    showView={true}
                    showDelete={shouldShowDeleteButton("cadFile", false, true)}
                    onDelete={() => handleRemoveExistingFile("cadFile")}
                  />
                )}

              <RemovedFileMessage
                show={
                  !!(
                    (projectDetails?.submission?.files?.cadFile ||
                      (draftFiles?.cadFile &&
                        !Array.isArray(draftFiles.cadFile))) &&
                    removedFiles.has("cadFile")
                  )
                }
              />

              {/* Show both when replacing */}
              {(projectDetails?.submission?.files?.cadFile ||
                (draftFiles?.cadFile && !Array.isArray(draftFiles.cadFile))) &&
                cadFile &&
                !removedFiles.has("cadFile") && (
                  <div className="flex flex-col gap-1 mt-2">
                    <div className="flex items-center justify-between hover:bg-primary-foreground p-1 rounded-sm">
                      <span className="text-sm text-blue-600 line-through">
                        {projectDetails?.submission?.files?.cadFile?.name ||
                          (!Array.isArray(draftFiles?.cadFile) &&
                            draftFiles?.cadFile?.name)}{" "}
                        (will be replaced)
                      </span>
                      {(projectDetails?.submission?.files?.cadFile?.url ||
                        (!Array.isArray(draftFiles?.cadFile) &&
                          draftFiles?.cadFile?.url)) && (
                        <a
                          href={
                            projectDetails?.submission?.files?.cadFile?.url ||
                            (!Array.isArray(draftFiles?.cadFile) &&
                              draftFiles?.cadFile?.url) ||
                            "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center ml-2 gap-1 text-xs text-blue-500 underline"
                        >
                          <EyeIcon className="h-4 w-4" /> View File
                        </a>
                      )}
                    </div>
                    <SingleFileDisplay
                      file={cadFile}
                      fileName={cadFile.name}
                      fileSize={cadFile.size / 1024 / 1024}
                      showDelete={shouldShowDeleteButton("cadFile", true, true)}
                      onDelete={() => handleFileChange("cadFile", null)}
                    />
                  </div>
                )}

              {/* Show only new */}
              {((!projectDetails?.submission?.files?.cadFile &&
                (!draftFiles?.cadFile || Array.isArray(draftFiles.cadFile))) ||
                removedFiles.has("cadFile")) &&
                cadFile && (
                  <SingleFileDisplay
                    file={cadFile}
                    fileName={cadFile.name}
                    fileSize={cadFile.size / 1024 / 1024}
                    showDelete={shouldShowDeleteButton("cadFile", true, false)}
                    onDelete={() => handleFileChange("cadFile", null)}
                  />
                )}
            </div>

            <div className="space-y-2 mt-6">
              <Label htmlFor="bom">
                Prelim Or BOM & Manufacturer Info{" "}
                <span className="text-red-500 ml-1">*</span>
              </Label>

              <FileUploadZone
                id="filesbom"
                title="Upload BOM"
                description="PDF, Excel, Word, PNG, or JPEG file"
                acceptedFormats="PDF, XLSX, DOCX, PNG, JPEG"
                accept=".pdf,.xlsx,.docx,.png,.jpg,.jpeg"
                disabled={false}
                onBrowseClick={() =>
                  document.getElementById("files.bom")?.click()
                }
              />

              <input
                id="files.bom"
                type="file"
                hidden
                accept=".pdf,.xlsx,.docx,.png,.jpg,.jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  if (file) handleFileChange("bom", file);
                }}
              />

              <input
                type="text"
                {...form.register("files.bom")}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  height: 0,
                }}
                tabIndex={-1}
              />

              <FileErrorMessage
                message={form.formState.errors.files?.bom?.message}
              />

              {/* Show existing file only */}
              {(projectDetails?.submission?.files?.bom ||
                (draftFiles?.bom && !Array.isArray(draftFiles.bom))) &&
                !bomFile &&
                !removedFiles.has("bom") && (
                  <ExistingFileDisplay
                    fileName={
                      projectDetails?.submission?.files?.bom?.name ||
                      (!Array.isArray(draftFiles?.bom) &&
                        draftFiles?.bom?.name) ||
                      ""
                    }
                    fileUrl={
                      projectDetails?.submission?.files?.bom?.url ||
                      (!Array.isArray(draftFiles?.bom) && draftFiles?.bom?.url)
                    }
                    isDraft={
                      !!(
                        !projectDetails?.submission?.files?.bom &&
                        draftFiles?.bom
                      )
                    }
                    showView={true}
                    showDelete={shouldShowDeleteButton("bom", false, true)}
                    onDelete={() => handleRemoveExistingFile("bom")}
                  />
                )}

              <RemovedFileMessage
                show={
                  !!(
                    (projectDetails?.submission?.files?.bom ||
                      (draftFiles?.bom && !Array.isArray(draftFiles.bom))) &&
                    removedFiles.has("bom")
                  )
                }
              />

              {/* Show both when replacing */}
              {(projectDetails?.submission?.files?.bom ||
                (draftFiles?.bom && !Array.isArray(draftFiles.bom))) &&
                bomFile &&
                !removedFiles.has("bom") && (
                  <div className="flex flex-col gap-1 mt-2">
                    <div className="flex items-center justify-between hover:bg-primary-foreground p-1 rounded-sm">
                      <span className="text-sm text-blue-600 line-through">
                        {projectDetails?.submission?.files?.bom?.name ||
                          (!Array.isArray(draftFiles?.bom) &&
                            draftFiles?.bom?.name)}{" "}
                        (will be replaced)
                      </span>
                      {(projectDetails?.submission?.files?.bom?.url ||
                        (!Array.isArray(draftFiles?.bom) &&
                          draftFiles?.bom?.url)) && (
                        <a
                          href={
                            projectDetails?.submission?.files?.bom?.url ||
                            (!Array.isArray(draftFiles?.bom) &&
                              draftFiles?.bom?.url) ||
                            "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center ml-2 gap-1 text-xs text-blue-500 underline"
                        >
                          <EyeIcon className="h-4 w-4" /> View File
                        </a>
                      )}
                    </div>
                    <SingleFileDisplay
                      file={bomFile}
                      fileName={bomFile.name}
                      fileSize={bomFile.size / 1024 / 1024}
                      showDelete={shouldShowDeleteButton("bom", true, true)}
                      onDelete={() => handleFileChange("bom", null)}
                    />
                  </div>
                )}

              {/* Show only new */}
              {((!projectDetails?.submission?.files?.bom &&
                (!draftFiles?.bom || Array.isArray(draftFiles.bom))) ||
                removedFiles.has("bom")) &&
                bomFile && (
                  <SingleFileDisplay
                    file={bomFile}
                    fileName={bomFile.name}
                    fileSize={bomFile.size / 1024 / 1024}
                    showDelete={shouldShowDeleteButton("bom", true, false)}
                    onDelete={() => handleFileChange("bom", null)}
                  />
                )}
            </div>

            {/* P.E. Stamp Question */}
            <div className="space-y-4">
              <Label className="text-base font-medium">
                Do we need to provide P.E. Stamp for the Permit?
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <RadioGroup
                value={form.watch("peStampRequired")}
                onValueChange={(value) =>
                  form.setValue(
                    "peStampRequired",
                    value as "no" | "yes" | "not-sure"
                  )
                }
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="pe-stamp-no" />
                  <Label htmlFor="pe-stamp-no">
                    No, we will handle engineering.
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="pe-stamp-yes" />
                  <Label htmlFor="pe-stamp-yes">
                    Yes, please provide the P.E. Stamp
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="not-sure" id="pe-stamp-not-sure" />
                  <Label htmlFor="pe-stamp-not-sure">Not Sure</Label>
                </div>
              </RadioGroup>
              {form.formState.errors.peStampRequired && (
                <p className="text-sm text-red-600">
                  {form.formState.errors.peStampRequired.message}
                </p>
              )}
            </div>

            <div className="space-y-8">
              {/* Option 1: Fill out license details */}
              <div className="space-y-2">
                <Label className="font-large">Contractor License Info</Label>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">
                      Company Name<span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Enter company name"
                      {...form.register("contractorCompany")}
                    />
                    {form.formState.errors.contractorCompany && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.contractorCompany.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contractorAddress">
                      Address<span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="contractorAddress"
                      placeholder="Enter address"
                      {...form.register("contractorAddress")}
                    />
                    {form.formState.errors.contractorAddress && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.contractorAddress.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contractorLicenseNo">License #</Label>
                    <Input
                      id="contractorLicenseNo"
                      type="text"
                      placeholder="Enter license number"
                      {...form.register("contractorLicenseNo")}
                    />
                    {form.formState.errors.contractorLicenseNo && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.contractorLicenseNo.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contractorPhone">
                      Phone <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="contractorPhone"
                      type="tel"
                      placeholder="e.g. +12345678900 or 1234567890"
                      inputMode="tel"
                      {...form.register("contractorPhone")}
                    />
                    {form.formState.errors.contractorPhone && (
                      <p className="text-sm text-red-600">
                        {form.formState.errors.contractorPhone.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* OR Option 2: Upload PDF */}
              <div className="space-y-2">
                {/* checkbox to toggle upload */}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="uploadLicense"
                    checked={form.watch("uploadLicense")}
                    onCheckedChange={(checked) =>
                      form.setValue("uploadLicense", !!checked)
                    }
                    {...form.register("uploadLicense")}
                  />
                  <Label htmlFor="uploadLicense">
                    Upload License Info (Optional)
                  </Label>
                </div>

                {form.formState.errors.uploadLicense && (
                  <p className="text-sm text-red-600 mt-2">
                    {form.formState.errors.uploadLicense.message}
                  </p>
                )}

                {uploadLicense && (
                  <>
                    <FileUploadZone
                      id="filescontractorLicense"
                      title="Upload License Info"
                      description="PDF file"
                      acceptedFormats="PDF"
                      accept=".pdf"
                      disabled={false}
                      onBrowseClick={() =>
                        document.getElementById("license-upload")?.click()
                      }
                    />

                    <input
                      id="license-upload"
                      type="file"
                      hidden
                      accept=".pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        if (file) handleFileChange("contractorLicense", file);
                      }}
                    />

                    <input
                      type="text"
                      {...form.register("files.contractorLicense")}
                      style={{
                        position: "absolute",
                        opacity: 0,
                        pointerEvents: "none",
                        height: 0,
                      }}
                      tabIndex={-1}
                    />

                    <FileErrorMessage
                      message={
                        form.formState.errors.files?.contractorLicense?.message
                      }
                    />

                    {/* Show existing file only */}
                    {(projectDetails?.submission?.files?.contractorLicense ||
                      (draftFiles?.contractorLicense &&
                        !Array.isArray(draftFiles.contractorLicense))) &&
                      !licenseFile &&
                      !removedFiles.has("contractorLicense") && (
                        <ExistingFileDisplay
                          fileName={
                            projectDetails?.submission?.files?.contractorLicense
                              ?.name ||
                            (!Array.isArray(draftFiles?.contractorLicense) &&
                              draftFiles?.contractorLicense?.name) ||
                            ""
                          }
                          fileUrl={
                            projectDetails?.submission?.files?.contractorLicense
                              ?.url ||
                            (!Array.isArray(draftFiles?.contractorLicense) &&
                              draftFiles?.contractorLicense?.url)
                          }
                          isDraft={
                            !!(
                              !projectDetails?.submission?.files
                                ?.contractorLicense &&
                              draftFiles?.contractorLicense
                            )
                          }
                          showView={true}
                          showDelete={shouldShowDeleteButton(
                            "contractorLicense",
                            false,
                            true
                          )}
                          onDelete={() =>
                            handleRemoveExistingFile("contractorLicense")
                          }
                        />
                      )}

                    <RemovedFileMessage
                      show={
                        !!(
                          (projectDetails?.submission?.files
                            ?.contractorLicense ||
                            (draftFiles?.contractorLicense &&
                              !Array.isArray(draftFiles.contractorLicense))) &&
                          removedFiles.has("contractorLicense")
                        )
                      }
                    />

                    {/* Show both when replacing */}
                    {(projectDetails?.submission?.files?.contractorLicense ||
                      (draftFiles?.contractorLicense &&
                        !Array.isArray(draftFiles.contractorLicense))) &&
                      licenseFile &&
                      !removedFiles.has("contractorLicense") && (
                        <div className="flex flex-col gap-1 mt-2">
                          <div className="flex items-center justify-between hover:bg-primary-foreground p-1 rounded-sm">
                            <span className="text-sm text-blue-600 line-through">
                              {projectDetails?.submission?.files
                                ?.contractorLicense?.name ||
                                (!Array.isArray(
                                  draftFiles?.contractorLicense
                                ) &&
                                  draftFiles?.contractorLicense?.name)}{" "}
                              (will be replaced)
                            </span>
                            {(projectDetails?.submission?.files
                              ?.contractorLicense?.url ||
                              (!Array.isArray(draftFiles?.contractorLicense) &&
                                draftFiles?.contractorLicense?.url)) && (
                              <a
                                href={
                                  projectDetails?.submission?.files
                                    ?.contractorLicense?.url ||
                                  (!Array.isArray(
                                    draftFiles?.contractorLicense
                                  ) &&
                                    draftFiles?.contractorLicense?.url) ||
                                  "#"
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center ml-2 gap-1 text-xs text-blue-500 underline"
                              >
                                <EyeIcon className="h-4 w-4" /> View File
                              </a>
                            )}
                          </div>
                          <SingleFileDisplay
                            file={licenseFile}
                            fileName={licenseFile.name}
                            fileSize={licenseFile.size / 1024 / 1024}
                            showDelete={shouldShowDeleteButton(
                              "contractorLicense",
                              true,
                              true
                            )}
                            onDelete={() =>
                              handleFileChange("contractorLicense", null)
                            }
                          />
                        </div>
                      )}

                    {/* Show only new */}
                    {licenseFile &&
                      ((!projectDetails?.submission?.files?.contractorLicense &&
                        (!draftFiles?.contractorLicense ||
                          Array.isArray(draftFiles.contractorLicense))) ||
                        removedFiles.has("contractorLicense")) && (
                        <SingleFileDisplay
                          file={licenseFile}
                          fileName={licenseFile.name}
                          fileSize={licenseFile.size / 1024 / 1024}
                          showDelete={shouldShowDeleteButton(
                            "contractorLicense",
                            true,
                            false
                          )}
                          onDelete={() =>
                            handleFileChange("contractorLicense", null)
                          }
                        />
                      )}
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="architectural-upload">
                Architectural Building Files (Nice‑to‑Have)
              </Label>

              <FileUploadZone
                id="filesarchitecturalFile"
                title="Upload Architectural Files"
                description="DWG or PDF files - Multiple files allowed"
                acceptedFormats="DWG, PDF"
                accept=".dwg,.pdf"
                multiple={true}
                disabled={false}
                onBrowseClick={() =>
                  document.getElementById("architectural-upload")?.click()
                }
              />

              <input
                id="architectural-upload"
                type="file"
                hidden
                accept=".dwg,.pdf"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach((file) =>
                    handleFileChange("architecturalFile", file)
                  );
                  e.target.value = ""; // Reset input for re-selection
                }}
              />

              <input
                type="text"
                {...form.register("files.architecturalFile")}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  height: 0,
                }}
                tabIndex={-1}
              />

              <FileErrorMessage
                message={
                  form.formState.errors.files?.architecturalFile?.message
                }
              />

              {/* Show existing submission files */}
              {projectDetails?.submission?.files?.architecturalFile &&
                !removedFiles.has("architecturalFile") && (
                  <CompactExistingFilesList
                    files={
                      Array.isArray(
                        projectDetails.submission.files.architecturalFile
                      )
                        ? projectDetails.submission.files.architecturalFile.filter(
                            (file) =>
                              !removedExistingFiles.has(
                                file.blobName || file.name
                              )
                          )
                        : [projectDetails.submission.files.architecturalFile]
                    }
                    isDraft={false}
                    showView={true}
                    showDelete={shouldShowDeleteButton(
                      "architecturalFile",
                      false,
                      true
                    )}
                    onDeleteFile={(blobName) =>
                      handleRemoveExistingArrayFile(blobName)
                    }
                  />
                )}

              {/* Show draft files */}
              {draftFiles?.architecturalFile &&
                Array.isArray(draftFiles.architecturalFile) &&
                !projectDetails?.submission?.files?.architecturalFile &&
                !removedFiles.has("architecturalFile") && (
                  <CompactExistingFilesList
                    files={draftFiles.architecturalFile}
                    isDraft={true}
                    showView={true}
                    showDelete={true}
                    onDeleteFile={() =>
                      handleRemoveExistingFile("architecturalFile")
                    }
                  />
                )}

              {/* Show new files */}
              {architecturalFile &&
                Array.isArray(architecturalFile) &&
                architecturalFile.length > 0 && (
                  <CompactMultipleFilesList
                    files={architecturalFile}
                    showDelete={true}
                    onDeleteFile={(index) =>
                      handleMultipleFileRemove("architecturalFile", index)
                    }
                  />
                )}

              <RemovedFileMessage
                show={
                  !!(
                    projectDetails?.submission?.files?.architecturalFile &&
                    removedFiles.has("architecturalFile")
                  )
                }
                message="Files removed - will be deleted on save"
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(`/projects/${projectId}`)}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={createSubmissionMutation.isPending}>
          {createSubmissionMutation.isPending ||
          updateSubmissionMutation.isPending
            ? projectDetails
              ? "Updating your application..."
              : "Submitting your application..."
            : projectDetails
            ? "Update Permit Application"
            : "Submit Permit Application"}
        </Button>
      </div>
    </form>
  );
};

export default SubmissionForm;
